import React from 'react';
import { BottomSheet, Card, Badge, Alert } from '@parsina/framework';
import { ShieldAlert } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import {
  severityKey, warningTypeKey, warningSourceKey, severityVariant, sortWarnings,
} from '../../constants/vocabulary.js';
import { AiAnswerNote } from '../common/PracticeNotice.jsx';

// «بررسی تداخل» — the one AI answer in this app that is NOT paraphrased.
//
// core.md §8.4: «Interaction warnings are forwarded verbatim — no second LLM
// pass to soften them.» That rule reaches all the way down here: the description
// is printed as it arrived, the severity is printed as it arrived, and an
// unrecognised severity is printed RAW rather than folded onto «متوسط» — folding
// it would be a rewrite of a deterministic safety warning performed in a browser
// (which is why `severityKey` and friends are partial mappers).
//
// ── The skipped list is part of the answer, not a footnote ───────────────
//
// The check runs over rx items that carry a formulary slug. A row the student
// typed by hand has no slug and is SKIPPED — and a screen that showed «تداخلی
// یافت نشد» over a prescription half of whose rows were never examined would be
// lying by omission. `skippedDrugNames` comes back from the BFF for exactly this
// and is rendered every time it is non-empty, including when there were no
// warnings at all.

export default function InteractionSheet({ open, onClose, result }) {
  const { t } = useTranslation();
  if (!open) return null;

  // Most severe first, with anything unrecognised kept at the END rather than dropped.
  const warnings = sortWarnings(result?.warnings || []);
  const skipped = result?.skippedDrugNames || [];

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('visit.rx.interactionTitle')}
      fullHeight
      maxWidth={720}
      closeLabel={t('common.actions.close')}
      data-testid="interaction-sheet"
    >
      {!warnings.length && (
        <Alert variant="success" title={t('visit.rx.interactionNone')} data-testid="interaction-none">
          {t('visit.rx.interactionNoneBody')}
        </Alert>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: warnings.length ? 0 : 12 }}>
        {warnings.map((w, i) => {
          const sev = severityKey(w.severity);
          const type = warningTypeKey(w.warningType);
          const source = warningSourceKey(w.source);
          return (
            <Card
              key={`${w.description}-${i}`}
              style={{ padding: 12, borderColor: w.severity === 'severe' ? C.red : undefined }}
              data-testid={`interaction-warning-${i}`}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <ShieldAlert
                  size={16}
                  aria-hidden
                  color={w.severity === 'severe' ? C.red : C.teal}
                />
                {/* Raw when unrecognised — see the header. */}
                <Badge variant={severityVariant(w.severity)}>
                  {sev ? t(sev) : w.severity}
                </Badge>
                {w.warningType && (
                  <Badge variant="outline">{type ? t(type) : w.warningType}</Badge>
                )}
                {w.drugPair && (
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: C.textPrimary }}>
                    {w.drugPair}
                  </span>
                )}
                {w.drug && !w.drugPair && (
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: C.textPrimary }}>{w.drug}</span>
                )}
              </div>
              {/* VERBATIM. */}
              <p style={{
                margin: '8px 0 0', fontSize: 13, lineHeight: 2, color: C.textPrimary,
                whiteSpace: 'pre-wrap',
              }}
              >
                {w.description}
              </p>
              {w.condition && (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: CT.ink2 }}>{w.condition}</p>
              )}
              {w.source && (
                <p style={{ margin: '6px 0 0', fontSize: 11.5, color: CT.mute }}>
                  {t('visit.rx.interactionSource')}
                  {': '}
                  {source ? t(source) : w.source}
                </p>
              )}
            </Card>
          );
        })}
      </div>

      {!!skipped.length && (
        <Alert
          variant="warning"
          title={t('visit.rx.skippedTitle')}
          style={{ marginTop: 12 }}
          data-testid="interaction-skipped"
        >
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.9 }}>
            {t('visit.rx.skippedBody')}
          </p>
          <ul style={{ margin: '6px 0 0', paddingInlineStart: 18, fontSize: 12.5 }}>
            {skipped.map((name) => <li key={name}>{name}</li>)}
          </ul>
        </Alert>
      )}

      <AiAnswerNote />
    </BottomSheet>
  );
}
